import React from 'react';

import type { IntlShape } from 'react-intl';

import { convertToInlineCss } from '@atlaskit/editor-common/lazy-node-view';
import type { PortalProviderAPI } from '@atlaskit/editor-common/portal';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import {
	PluginKey,
	type EditorState,
	type ReadonlyTransaction,
} from '@atlaskit/editor-prosemirror/state';
import { Decoration, DecorationSet } from '@atlaskit/editor-prosemirror/view';

import type { AiSuggestionsPlugin, Suggestion } from '../aiSuggestionsPluginType';
import { SuggestionIconButton } from '../ui/components/SuggestionIconButton';
import { buildSuggestionMaps } from '../ui/utils/buildSuggestionMaps';
import { updateSuggestions } from '../ui/utils/updateSuggestions';

const nodeTopAdjustments: Record<string, number> = {
	table: 24,
	paragraph: 14,
	bulletList: 16,
	orderedList: 16,
	decisionList: 12,
	taskList: 8,
	codeBlock: 12,
	panel: 12,
	rule: 18,
	mediaSingle: 24,
	media: 24,
	bodiedExtension: 0,
	extension: 12,
	heading1: 51,
	heading2: 42,
	heading3: 35,
	heading4: 19,
	heading5: 19,
	heading6: 15,
	layoutSection: 16,
	blockquote: 12,
	expand: 4,
	nestedExpand: 1,
};

export type AiSuggestionsPluginState = {
	baseLocalIdBySuggestionId: Map<string, string>;
	decorations: DecorationSet;
	orderedSuggestionIds: string[];
	selectedSuggestion: string | undefined;
	suggestionById: Map<string, Suggestion>;
	suggestionIdsByBaseLocalId: Map<string, Set<string>>;
};

export const aiSuggestionsPluginKey = new PluginKey<AiSuggestionsPluginState>(
	'aiSuggestionsPlugin',
);

export type AiSuggestionsMeta =
	| { action: 'SET_SUGGESTIONS'; suggestions: Suggestion[] }
	| { action: 'CLEAR_SUGGESTIONS' }
	| { action: 'SELECT_SUGGESTION'; id: string | undefined }
	| { action: 'REMOVE_SUGGESTION'; id: string };

type DocRange = {
	from: number;
	to: number;
};

const pointTouchesRange = (point: number, range: DocRange): boolean =>
	point >= range.from && point <= range.to;

const rangesOverlap = (rangeA: DocRange, rangeB: DocRange): boolean => {
	if (rangeA.from === rangeA.to) {
		return pointTouchesRange(rangeA.from, rangeB);
	}

	if (rangeB.from === rangeB.to) {
		return pointTouchesRange(rangeB.from, rangeA);
	}

	return rangeA.from < rangeB.to && rangeB.from < rangeA.to;
};

const getDecoratedNodeRanges = (
	doc: PMNode,
	suggestionIdsByBaseLocalId: Map<string, Set<string>>,
): DocRange[] => {
	const decoratedNodeRanges: DocRange[] = [];

	doc.descendants((node, pos) => {
		if (suggestionIdsByBaseLocalId.has(node.attrs?.localId)) {
			decoratedNodeRanges.push({ from: pos, to: pos + node.nodeSize });
			return false;
		}

		return true;
	});

	return decoratedNodeRanges;
};

const docChangeAffectsDecorations = ({
	doc,
	suggestionIdsByBaseLocalId,
	tr,
}: {
	doc: PMNode;
	suggestionIdsByBaseLocalId: Map<string, Set<string>>;
	tr: ReadonlyTransaction;
}): boolean => {
	if (!tr.docChanged || suggestionIdsByBaseLocalId.size === 0) {
		return false;
	}

	const decoratedNodeRanges = getDecoratedNodeRanges(doc, suggestionIdsByBaseLocalId);
	if (decoratedNodeRanges.length === 0) {
		return false;
	}

	return tr.mapping.maps.some((stepMap) => {
		let affectsDecorations = false;

		stepMap.forEach((oldStart, oldEnd) => {
			if (affectsDecorations) {
				return;
			}

			const changedRange = { from: oldStart, to: oldEnd };
			affectsDecorations = decoratedNodeRanges.some((decoratedRange) =>
				rangesOverlap(changedRange, decoratedRange),
			);
		});

		return affectsDecorations;
	});
};

function calculateSuggestionsDecorations({
	doc,
	suggestionById,
	suggestionIdsByBaseLocalId,
	api,
	intl,
	selectedSuggestion,
	nodeViewPortalProviderAPI,
}: {
	api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined;
	doc: PMNode;
	intl: IntlShape | undefined;
	nodeViewPortalProviderAPI: PortalProviderAPI;
	selectedSuggestion: string | undefined;
	suggestionById: Map<string, Suggestion>;
	suggestionIdsByBaseLocalId: Map<string, Set<string>>;
}): DecorationSet {
	if (suggestionById.size === 0) {
		return DecorationSet.empty;
	}

	const decorations: Decoration[] = [];

	doc.descendants((node, pos) => {
		const { localId } = node.attrs;
		if (localId) {
			const suggestionsAtLocalId = suggestionIdsByBaseLocalId.get(localId);
			if (suggestionsAtLocalId?.size) {
				const firstSuggestionId = suggestionsAtLocalId.values().next().value;
				const firstSuggestion = firstSuggestionId
					? suggestionById.get(firstSuggestionId)
					: undefined;

				if (firstSuggestion) {
					const element = document.createElement('span');
					const nodeTypeName =
						node.type.name === 'heading' ? 'heading' + node.attrs.level : node.type.name;
					element.setAttribute(
						'style',
						convertToInlineCss({
							position: 'relative',
							width: '100%',
							display: 'block',
							pointerEvents: 'none',
						}),
					);

					const key = crypto.randomUUID();
					const isSelected = Boolean(
						selectedSuggestion && suggestionsAtLocalId.has(selectedSuggestion),
					);

					// Tables don't use breakout for resizing, the width is stored in the attrs
					const leftAdjustment =
						node.type.name === 'table' && typeof node.attrs?.width === 'number'
							? (node.attrs.width - 760) / 2
							: 0;

					decorations.push(
						Decoration.widget(
							pos,
							() => {
								nodeViewPortalProviderAPI.render(
									() => (
										<SuggestionIconButton
											leftAdjustment={leftAdjustment}
											topAdjustment={nodeTopAdjustments[nodeTypeName] ?? 0}
											suggestion={firstSuggestion}
											suggestionsCount={suggestionsAtLocalId?.size}
											isSelected={isSelected}
											api={api}
											intl={intl}
										/>
									),
									element,
									key,
								);
								return element;
							},
							{
								side: -2,
								key,
								marks: node.marks.filter((mark) => mark.type.name === 'breakout'),
								destroy: () => {
									// Clean up properly
									// nodeViewPortalProviderAPI.remove(key);
								},
							},
						),
					);
				}
			}
		}
		return false;
	});

	if (!decorations.length) {
		return DecorationSet.empty;
	}

	return DecorationSet.create(doc, decorations);
}

const {
	suggestionById: initSuggestionById,
	suggestionIdsByBaseLocalId: initSuggestionIdsByBaseLocalId,
	baseLocalIdBySuggestionId: initBaseLocalIdBySuggestionId,
	orderedSuggestionIds: initOrderedSuggestionIds,
} = buildSuggestionMaps(undefined);

const initState: AiSuggestionsPluginState = {
	decorations: DecorationSet.empty,
	selectedSuggestion: undefined,
	suggestionById: initSuggestionById,
	suggestionIdsByBaseLocalId: initSuggestionIdsByBaseLocalId,
	baseLocalIdBySuggestionId: initBaseLocalIdBySuggestionId,
	orderedSuggestionIds: initOrderedSuggestionIds,
};

export function createPlugin({
	api,
	intl,
	contentAri,
	nodeViewPortalProviderAPI,
}: {
	api?: ExtractInjectionAPI<AiSuggestionsPlugin>;
	contentAri?: string;
	intl?: IntlShape;
	nodeViewPortalProviderAPI: PortalProviderAPI;
}): SafePlugin<AiSuggestionsPluginState> {
	return new SafePlugin<AiSuggestionsPluginState>({
		key: aiSuggestionsPluginKey,
		state: {
			init(_config, _state: EditorState): AiSuggestionsPluginState {
				return initState;
			},
			apply(
				tr: ReadonlyTransaction,
				pluginState: AiSuggestionsPluginState,
				_oldState: EditorState,
				newState: EditorState,
			): AiSuggestionsPluginState {
				const meta = tr.getMeta(aiSuggestionsPluginKey) as AiSuggestionsMeta | undefined;
				let nextState = pluginState;

				if (meta) {
					switch (meta.action) {
						case 'SET_SUGGESTIONS': {
							const {
								suggestionById,
								baseLocalIdBySuggestionId,
								suggestionIdsByBaseLocalId,
								orderedSuggestionIds,
							} = buildSuggestionMaps({
								suggestions: meta.suggestions,
								doc: newState.doc,
							});

							nextState = {
								...pluginState,
								suggestionById,
								suggestionIdsByBaseLocalId,
								baseLocalIdBySuggestionId,
								orderedSuggestionIds,
								decorations: calculateSuggestionsDecorations({
									doc: newState.doc,
									suggestionById,
									suggestionIdsByBaseLocalId,
									api,
									intl,
									selectedSuggestion: pluginState.selectedSuggestion,
									nodeViewPortalProviderAPI,
								}),
							};
							break;
						}
						case 'CLEAR_SUGGESTIONS': {
							const {
								suggestionById,
								baseLocalIdBySuggestionId,
								suggestionIdsByBaseLocalId,
								orderedSuggestionIds,
							} = buildSuggestionMaps({
								doc: newState.doc,
								suggestions: [],
							});
							nextState = {
								...pluginState,
								suggestionById,
								suggestionIdsByBaseLocalId,
								baseLocalIdBySuggestionId,
								orderedSuggestionIds,
								selectedSuggestion: undefined,
								decorations: DecorationSet.empty,
							};
							break;
						}
						case 'SELECT_SUGGESTION':
							nextState = {
								...pluginState,
								selectedSuggestion: meta.id,
								decorations: calculateSuggestionsDecorations({
									doc: newState.doc,
									suggestionById: pluginState.suggestionById,
									suggestionIdsByBaseLocalId: pluginState.suggestionIdsByBaseLocalId,
									api,
									intl,
									selectedSuggestion: meta.id,
									nodeViewPortalProviderAPI,
								}),
							};
							break;
						case 'REMOVE_SUGGESTION': {
							const {
								suggestionById,
								baseLocalIdBySuggestionId,
								suggestionIdsByBaseLocalId,
								orderedSuggestionIds,
							} = pluginState;
							const removedId = meta.id;
							const suggestion = suggestionById.get(removedId);

							if (!suggestion) {
								nextState = {
									...pluginState,
									decorations: calculateSuggestionsDecorations({
										doc: newState.doc,
										suggestionById,
										suggestionIdsByBaseLocalId,
										api,
										intl,
										selectedSuggestion: undefined,
										nodeViewPortalProviderAPI,
									}),
								};
								break;
							}

							suggestionById.delete(removedId);

							const baseLocalId = baseLocalIdBySuggestionId.get(removedId);
							baseLocalIdBySuggestionId.delete(removedId);

							if (baseLocalId) {
								suggestionIdsByBaseLocalId.get(baseLocalId)?.delete(removedId);
							}

							nextState = {
								...pluginState,
								suggestionById,
								baseLocalIdBySuggestionId,
								suggestionIdsByBaseLocalId,
								orderedSuggestionIds: orderedSuggestionIds.filter((id) => id !== removedId),
								selectedSuggestion: undefined,
								decorations: calculateSuggestionsDecorations({
									doc: newState.doc,
									suggestionById,
									suggestionIdsByBaseLocalId,
									api,
									intl,
									selectedSuggestion: undefined,
									nodeViewPortalProviderAPI,
								}),
							};
							break;
						}
						default:
							break;
					}
				} else {
					const shouldRecalculateDecorations = docChangeAffectsDecorations({
						doc: _oldState.doc,
						suggestionIdsByBaseLocalId: pluginState.suggestionIdsByBaseLocalId,
						tr,
					});

					nextState = {
						...pluginState,
						decorations: shouldRecalculateDecorations
							? calculateSuggestionsDecorations({
									doc: newState.doc,
									suggestionById: pluginState.suggestionById,
									suggestionIdsByBaseLocalId: pluginState.suggestionIdsByBaseLocalId,
									api,
									intl,
									selectedSuggestion: pluginState.selectedSuggestion,
									nodeViewPortalProviderAPI,
							  })
							: pluginState.decorations.map(tr.mapping, tr.doc),
					};
				}

				return nextState;
			},
		},
		props: {
			decorations(state: EditorState) {
				return aiSuggestionsPluginKey.getState(state)?.decorations ?? DecorationSet.empty;
			},
		},
		view: () => {
			const abortController = new AbortController();
			if (contentAri && api) {
				updateSuggestions({ contentAri, api, generate: false, signal: abortController.signal });
			}
			return {
				destroy: () => {
					abortController.abort();
				},
			};
		},
	});
}
